import { useState, useEffect, useCallback } from 'react';
import { DateTime } from 'luxon';
import { makeRequest } from 'services/request.service';
import { useAuth } from 'auth/AuthContext';

const SUMMARY_TIME_ZONE = 'America/Mexico_City';

const getPreviousDate = () =>
  DateTime.now().setZone(SUMMARY_TIME_ZONE).minus({ days: 1 }).toFormat('yyyy-MM-dd');

const getCurrentDate = () => DateTime.now().setZone(SUMMARY_TIME_ZONE).toFormat('yyyy-MM-dd');

const getErrorMessage = error =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Failed to load workday summary data';

const normalizeWorkdaySummary = (data = {}) => ({
  farmActivityData: data.farmActivitySummary || data.farmActivityData || [],
  lastActivitiesData: data.lastActivitiesBoard || data.lastActivitiesData || [],
  workersByGroup: data.workersByGroup || [],
  workersInBlock: data.workersInBlock || [],
  totalWorkers: data.totalActiveWorkers ?? data.totalWorkers ?? 0
});

// GET request for workday summary data
const getWorkdaySummaryData = async (farmId, filterDate) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/workday-summary?filterDate=${filterDate}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching workday summary data:', error);
    throw error;
  }
};

export const useWorkdaySummary = (customFarmId = null, initialDate = null) => {
  const { user } = useAuth();

  // Use custom farmId if provided, otherwise fall back to user.farmId
  const farmId = customFarmId || user?.farmId;

  const [workdaySummary, setWorkdaySummary] = useState({
    totalWorkers: 0,
    workersByGroup: [],
    workersInBlock: [],
    farmActivityData: [],
    lastActivitiesData: [],
    filterDate: initialDate || getPreviousDate(),
    currentDate: getCurrentDate(),
    loading: true,
    error: null
  });

  const fetchWorkdaySummary = useCallback(async () => {
    if (!farmId) {
      return;
    }

    try {
      setWorkdaySummary(prev => ({ ...prev, loading: true, error: null }));

      const data = await getWorkdaySummaryData(farmId, workdaySummary.filterDate);
      const normalizedData = normalizeWorkdaySummary(data);

      setWorkdaySummary(prev => ({
        ...prev,
        ...normalizedData,
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching workday summary:', error);
      setWorkdaySummary(prev => ({
        ...prev,
        loading: false,
        error: getErrorMessage(error)
      }));
    }
  }, [farmId, workdaySummary.filterDate]);

  const updateFilterDate = newDate => {
    setWorkdaySummary(prev => ({
      ...prev,
      filterDate: newDate
    }));
  };

  useEffect(() => {
    fetchWorkdaySummary();
  }, [fetchWorkdaySummary]);

  return {
    data: workdaySummary,
    updateFilterDate,
    refetch: fetchWorkdaySummary
  };
};
